import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import Modal from '../ui/Modal';
import AppointmentItem from '../customers/AppointmentItem';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';

interface CustomerAppointmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: any;
  clientBookings: any[];
  getPerformanceById: (id: any) => any;
  getServiceById: (id: any) => any;
}

const CustomerAppointmentsModal: React.FC<CustomerAppointmentsModalProps> = ({
  isOpen,
  onClose,
  customer,
  clientBookings,
  getPerformanceById,
  getServiceById,
}) => {
  const { t, i18n } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(20);

  const groupedBookings = useMemo(() => {
    const groups: { monthTitle: string; monthKey: string; bookings: any[] }[] = [];
    
    clientBookings.slice(0, visibleCount).forEach((b) => {
      if (!b.date) return;
      const dateObj = new Date(`${b.date}T00:00:00`);
      const formatted = format(dateObj, 'MMMM yyyy', {
        locale: i18n.language === 'it' ? it : enUS
      });
      const monthTitle = formatted.charAt(0).toUpperCase() + formatted.slice(1);
      const monthKey = b.date.substring(0, 7); // "YYYY-MM"

      let group = groups.find(g => g.monthKey === monthKey);
      if (!group) {
        group = { monthTitle, monthKey, bookings: [] };
        groups.push(group);
      }
      group.bookings.push(b);
    });

    return groups;
  }, [clientBookings, visibleCount, i18n.language]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerClassName="bg-white border-b border-gray-100 pb-4"
      headerContent={
        <div>
          <span className="bg-clip-50 text-clip-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border border-clip-100 mb-1 inline-block">
            {t('customers.appointments', 'Storico Appuntamenti')}
          </span>
          <h2 className="text-2xl font-bold text-gray-900 leading-tight">
            {customer?.firstName} {customer?.lastName}
          </h2>
        </div>
      }
    >
      <div className="space-y-4">
        {clientBookings.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-2xl">
            <Calendar size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">{t('customers.modal.no_appointments')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedBookings.map((group) => (
              <div key={group.monthKey} className="space-y-2">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-wider px-1">
                  {group.monthTitle}
                </h3>
                <div className="space-y-2">
                  {group.bookings.map((b) => (
                    <AppointmentItem
                      key={b.id}
                      booking={b}
                      getPerformanceById={getPerformanceById}
                    />
                  ))}
                </div>
              </div>
            ))}

            {clientBookings.length > visibleCount && (
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 20)}
                className="w-full text-center py-2 text-xs font-semibold text-gray-400 underline mt-3"
              >
                {t('common.show_more')}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CustomerAppointmentsModal;
